/**
 * Access log tab — staffApi.security.events(200) filtered to role/staff
 * events plus MFA events, newest first. Row markup is the same as
 * MySecurityPage's security log (src/pages/security/MySecurityPage.tsx).
 */
import { useEffect, useState } from 'react';
import { staffApi, type SecurityEvent } from '../../../services/staffApi';
import { RubyLoader } from '../../../components/RubyLoader';

// Exactly the SecurityEventType union staff-service emits
// (services/shared/staff-service/src/lib/mfa.ts) — role/staff events plus
// the MFA events. Every one gets a human label here; anything not in this
// set (there shouldn't be any) falls back to a humanised version of the raw
// name rather than ever rendering snake_case.
const EVENT_LABEL: Record<string, string> = {
  role_created: 'Role created',
  role_updated: 'Role permissions changed',
  role_deleted: 'Role deleted',
  staff_role_changed: "Staff member's role changed",
  staff_invited: 'Staff member invited',
  staff_suspended: 'Staff member suspended',
  staff_deleted: 'Staff member removed',
  mfa_reset_by_admin: 'Two-step sign-in reset by an admin',
  recovery_code_used: 'Recovery code used',
  recovery_code_failed: 'Failed recovery attempt',
  authenticator_replaced: 'New authenticator set up',
  authenticator_enrolled: 'Authenticator set up',
  recovery_codes_regenerated: 'Recovery codes regenerated',
  recovery_expired: 'Recovery expired — signed out',
  mfa_not_enrolled: 'Signed out — no authenticator',
  email_mfa_revoked: 'Stray email code revoked',
};

// Filtering is independent of the label dictionary above: a role_*/staff_*
// event the dictionary hasn't caught up with yet (a new SecurityEventType)
// must still show up here — with its label humanised, never dropped — not
// silently disappear because it's missing a hand-written label.
const MFA_EVENTS = new Set([
  'mfa_reset_by_admin', 'recovery_code_used', 'recovery_code_failed', 'authenticator_replaced',
  'authenticator_enrolled', 'recovery_codes_regenerated', 'recovery_expired', 'mfa_not_enrolled', 'email_mfa_revoked',
]);

function isRelevant(event: string): boolean {
  return MFA_EVENTS.has(event) || event.startsWith('role_') || event.startsWith('staff_');
}

/** snake_case fallback for an event name this screen doesn't recognise yet — never render the raw string. */
function humanize(event: string): string {
  return event.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function labelFor(event: string): string {
  return EVENT_LABEL[event] || humanize(event);
}

export function AccessLog() {
  const [events, setEvents] = useState<SecurityEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    staffApi.security.events(200)
      .then((r) => { if (!cancelled) setEvents(r.events); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Could not load the access log'); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>;
  }
  if (!events) {
    return <RubyLoader variant="section" />;
  }

  const relevant = events
    .filter((e) => isRelevant(e.event))
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <div className="bg-white text-stone-900 border border-stone-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-stone-200 font-semibold text-stone-900">Access log</div>
      {relevant.length === 0 ? (
        <p className="px-5 py-4 text-sm text-stone-500">No role or access events yet.</p>
      ) : (
        <ul className="divide-y divide-stone-100">
          {relevant.map((ev) => (
            <li key={ev.id} className="px-5 py-3 text-sm flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className={`font-medium ${ev.event === 'recovery_code_failed' || ev.event === 'email_mfa_revoked' ? 'text-red-600' : 'text-stone-900'}`}>
                {labelFor(ev.event)}
              </span>
              <span className="text-stone-600">{ev.targetEmail || ev.targetLabel}</span>
              {ev.actorEmail && ev.actorEmail !== ev.targetEmail && <span className="text-stone-500">by {ev.actorEmail}</span>}
              {ev.detail && <span className="text-stone-500">{ev.detail}</span>}
              <span className="text-stone-400 text-xs ml-auto">{new Date(ev.at).toLocaleString()}{ev.ipAddress ? ` · ${ev.ipAddress}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AccessLog;
